import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'

import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete'
import { TagModule } from 'primeng/tag'
import { validate as uuidValidate } from 'uuid'

import { Observable } from 'rxjs'
import { finalize, map } from 'rxjs/operators'

import { FlxFormControlModule } from '@flowx/angular-ui-toolkit'
import { processStatusTagTypeMap } from '@flowx/designer/modules/core/dictionary/common.types'
import { UUID_LENGTH } from '@flowx/designer/modules/core/dictionary/constants'
import { FlxTableDateFormatPipe } from '@flowx/designer/pipes/flx-date.pipe'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'

import {
  InstanceAutocompleteOption,
  SelectedInstance,
  mapInstanceToOption,
} from './resource-instances-autocomplete.model'

export {
  InstanceAutocompleteOption,
  SelectedInstance,
} from './resource-instances-autocomplete.model'

function isValidUUID(uuid: string): boolean {
  if (!uuid) {
    return false
  }
  if (uuidValidate(uuid)) {
    return true
  }
  const oracleUUIDRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return oracleUUIDRegex.test(uuid)
}

@Component({
  selector: 'flx-resource-instances-autocomplete',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    TagModule,
    FlxFormControlModule,
    FlxTableDateFormatPipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './resource-instances-autocomplete.component.html',
})
export class FlxResourceInstancesAutocompleteComponent {
  #destroyRef = inject(DestroyRef)
  #processInstancesService = inject(ProcessInstanceService)
  #loadedInstances: InstanceAutocompleteOption[] = []
  #loading = false

  autocomplete = viewChild<AutoComplete>('autocomplete')

  loadInstances = input<() => Observable<InstanceAutocompleteOption[]>>()
  processDefinitionName = input<string>()
  disabled = input<boolean>(false)
  initialInstanceUuid = input<string>()
  placeholder = input<string>('')
  requireMatch = input<boolean>(false)

  selectedInstanceChanged = output<SelectedInstance>()

  instances = signal<InstanceAutocompleteOption[]>([])
  initialInstanceSet = false
  instanceModel: string | InstanceAutocompleteOption = ''
  selectedUuid = signal<string>('')

  constructor() {
    effect(() => {
      if (this.initialInstanceUuid() && !this.initialInstanceSet) {
        this.initialInstanceSet = true
        this.instanceModel = this.initialInstanceUuid()
      }
    })
  }

  showAutocompleteOptions(event: Event): void {
    if ((this.selectedUuid()?.length ?? 0) < UUID_LENGTH) {
      if (this.#loadedInstances.length) {
        this.instances.set([...this.#loadedInstances])
      } else {
        this.#fetchInstances()
      }
      this.autocomplete()?.handleDropdownClick(event)
    }
  }

  setInstance(event: string | InstanceAutocompleteOption): void {
    if (!event) {
      this.selectedUuid.set('')
      this.selectedInstanceChanged.emit({ instanceId: null, instanceUuid: '', valid: false })
      return
    }
    if (typeof event === 'string') {
      this.selectedUuid.set(event)
      const match = this.#loadedInstances.find((option) => option.uuid === event)
      this.selectedInstanceChanged.emit({
        instanceId: match?.instanceId ?? null,
        instanceUuid: event,
        valid: this.requireMatch() ? !!match && isValidUUID(event) : isValidUUID(event),
      })
    } else if (event.uuid !== this.selectedUuid()) {
      this.selectedUuid.set(event.uuid)
      this.selectedInstanceChanged.emit({
        instanceId: event.instanceId,
        instanceUuid: event.uuid,
        valid: true,
      })
    }
  }

  onSearch(query: string): void {
    if (this.#loadedInstances.length) {
      this.instances.set(this.#filter(query))
    } else {
      this.#fetchInstances(query)
    }
  }

  #fetchInstances(query = ''): void {
    if (this.#loading) {
      return
    }
    this.#loading = true
    this.#resolveLoader()()
      .pipe(
        finalize(() => (this.#loading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((instances) => {
        this.#loadedInstances = instances
        this.instances.set(this.#filter(query))
      })
  }

  #filter(query: string): InstanceAutocompleteOption[] {
    const normalized = query?.trim().toLowerCase()
    return normalized
      ? this.#loadedInstances.filter((option) => option.uuid.toLowerCase().includes(normalized))
      : [...this.#loadedInstances]
  }

  #resolveLoader(): () => Observable<InstanceAutocompleteOption[]> {
    return this.loadInstances() ?? (() => this.#loadProcessInstances())
  }

  #loadProcessInstances(): Observable<InstanceAutocompleteOption[]> {
    return this.#processInstancesService
      .getProcessInstances({ page: 0, size: 10 }, { definitionName: this.processDefinitionName() })
      .pipe(
        map((response) =>
          response.content.map((instance) =>
            mapInstanceToOption(
              {
                instanceId: null,
                uuid: instance.uuid,
                status: instance.state,
                dateStarted: instance.dateStarted,
              },
              processStatusTagTypeMap
            )
          )
        )
      )
  }
}
